<template>
	<div class="tw-flex tw-items-center tw-gap-1">
		<div
			class="tw-flex tw-items-center tw-rounded-xl tw-p-0.5 tw-ring-gray-400/[.20]"
			:class="computedClasses"
			:style="computedSizeStyles"
			@click="toggle"
		>
			<div
				:style="computedInnerSizeStyles"
				class="tw-rounded-full tw-shadow"
				:class="circleClasses"
			></div>
		</div>
		<slot name="label">
			<div
				v-if="label"
				:class="{ 'tw-cursor-pointer': !disabled, 'tw-cursor-not-allowed': disabled }"
				@click="toggle"
			>
				{{ label }}
			</div>
		</slot>
	</div>
</template>

<script setup>
import { computed } from 'vue';
import { useConfirm } from '@/v2/plugins/alert';

const props = defineProps({
	modelValue: {},
	trueValue: {
		type: [String, Boolean, Number],
		default: true,
	},
	falseValue: {
		type: [String, Boolean, Number],
		default: false,
	},
	size: {
		type: String,
		default: 'md',
	},
	disabled: {
		type: Boolean,
		default: false,
	},
	label: {
		type: String,
		default: '',
	},
	useConfirmOnCheck: {
		type: Boolean,
		default: false,
	},
	useConfirmOnUnCheck: {
		type: Boolean,
		default: false,
	},
	confirmMessages: {
		type: Object,
		default: {},
	},
	outline: {
		type: Boolean,
		default: true,
	},
});

const emit = defineEmits(['update:modelValue']);

const isChecked = computed(() => {
	if (Array.isArray(props.modelValue)) {
		return props.modelValue.some((val) => val === props.trueValue);
	} else {
		return props.modelValue === props.trueValue;
	}
});

const confirmToggle = useConfirm((value) => {
	emit('update:modelValue', value);
}, props.confirmMessages);

const toggle = () => {
	if (props.disabled) return;
	if (Array.isArray(props.modelValue)) {
		if (isChecked.value) {
			const idx = props.modelValue.findIndex((el) => el === props.trueValue);
			const res = [...props.modelValue];
			res.splice(idx, 1);
			emit('update:modelValue', res);
		} else {
			emit('update:modelValue', [...props.modelValue, props.trueValue]);
		}
	} else {
		if (isChecked.value) {
			if (props.useConfirmOnCheck) {
				confirmToggle(props.falseValue);
			} else emit('update:modelValue', props.falseValue);
		} else {
			if (props.useConfirmOnUnCheck) {
				confirmToggle(props.trueValue);
			} else emit('update:modelValue', props.trueValue);
		}
	}
};

const computedClasses = computed(() => {
	const res = [];
	if (!props.disabled && isChecked.value) {
		res.push('tw-bg-brand');
	} else {
		if (isChecked.value) {
			res.push('tw-bg-brand-200');
		} else if (!props.outline) res.push('tw-bg-gray-100');
	}
	if (!props.disabled) {
		res.push('tw-cursor-pointer active:tw-ring');
	} else {
		res.push('tw-cursor-not-allowed');
	}
	if (isChecked.value) {
		res.push('tw-justify-end');
	} else {
		res.push('tw-justify-start');
	}
	if (!isChecked.value && props.outline) {
		res.push('tw-bg-white');
		res.push('tw-border tw-border-gray-400');
	}
	return res;
});

const circleClasses = computed(() => {
	if (!props.outline) {
		return 'tw-bg-white';
	}
	const res = [];
	if (props.disabled) {
		res.push('tw-bg-white');
	} else {
		if (isChecked.value) {
			res.push('tw-bg-white');
		} else {
			res.push('tw-bg-gray-400');
		}
	}
	return res;
});

const computedSizeStyles = computed(() => {
	return sizeStyles[props.size];
});
const computedInnerSizeStyles = computed(() => {
	return innerSizeStyles[props.size];
});

const sizeStyles = {
	sm: { width: '36px', height: '20px' },
	md: { width: '44px', height: '24px' },
};

const innerSizeStyles = {
	sm: { width: '16px', height: '16px' },
	md: { width: '20px', height: '20px' },
};

defineExpose({
	toggle,
});
</script>

<style lang="scss" scoped></style>
